import { useRef, useState } from 'react';

import OccurrenceFormService from '@/components/assessments/forms/OccurrenceFormService';
import OccurrenceCard from '@/components/assessments/records/OccurrenceCard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import useStudentRecordsMutations from '@/data/hook/assessment/useStudentRecordsMutations';
import { useScrollIntoView } from '@/data/hook/useScrollIntoView';
import { OccurrenceRecord } from '@/utils/assessments/klassPedagogicalSheetRecordTypes';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { Skeleton } from '../../ui/daisy';

type StudentOccurrencesProps = {
  occurrences: OccurrenceRecord[];
  studentId: number;
  isLoading: boolean;
  error: unknown;
  invalidate: () => Promise<void>;
  scheduledLessonId: number;
  klassId: number;
  canEditReport: boolean;
  canManage: boolean;
  isCreatingOccurrence: boolean;
  onCloseOccurrenceForm: () => void;
  onCancelOccurrenceForm: () => void;
  toggleRow?: () => void;
};

export default function StudentOccurrences({
  occurrences,
  studentId,
  isLoading,
  error,
  invalidate,
  scheduledLessonId,
  klassId,
  canEditReport,
  canManage,
  isCreatingOccurrence,
  onCloseOccurrenceForm,
  onCancelOccurrenceForm,
  toggleRow,
}: StudentOccurrencesProps) {
  const [editingRecord, setEditingRecord] = useState<OccurrenceRecord>();
  const occurrenceFormRef = useRef<HTMLDivElement>(null);

  useScrollIntoView(occurrenceFormRef, {
    condition: isCreatingOccurrence || !!editingRecord,
  });

  const { deleteOccurrence } = useStudentRecordsMutations({
    onSuccess: invalidate,
  });

  const onDeleteOccurrence = async (record: OccurrenceRecord) => {
    await deleteOccurrence(record);

    if (occurrences.length === 1) {
      toggleRow?.();
    }
  };

  const cancelForm = () => {
    setEditingRecord(undefined);
    onCancelOccurrenceForm();
  };

  const handleSuccess = async () => {
    await invalidate();
    setEditingRecord(undefined);
    onCloseOccurrenceForm();
  };

  const occurrenceForm = (record?: OccurrenceRecord) => (
    <div ref={occurrenceFormRef} className="rounded-xl bg-primary-content p-5">
      <OccurrenceFormService
        studentId={studentId}
        klassId={klassId}
        scheduledLessonId={scheduledLessonId}
        record={record}
        onSuccess={handleSuccess}
        onCancel={cancelForm}
        variant="compact"
      />
    </div>
  );

  if (error) {
    return (
      <span className="text-primary text-14">{getErrorMessage(error)}</span>
    );
  }

  if (isLoading) {
    return <Skeleton className="bg-primary-content rounded-xl w-full h-30" />;
  }

  return (
    <div className="flex flex-col gap-4">
      <ConditionalRenderer condition={occurrences.length}>
        <ul className="flex flex-col gap-4">
          {occurrences.map(record => (
            <li key={record.id} className="max-w-158.5">
              <ConditionalRenderer
                condition={editingRecord?.id !== record.id}
                fallback={occurrenceForm(record)}
              >
                <OccurrenceCard
                  record={record}
                  canManage={canEditReport && canManage}
                  showStudent={false}
                  onEdit={setEditingRecord}
                  onDelete={onDeleteOccurrence}
                />
              </ConditionalRenderer>
            </li>
          ))}
        </ul>
      </ConditionalRenderer>

      <ConditionalRenderer condition={canEditReport && isCreatingOccurrence}>
        <div className="max-w-158.5">{occurrenceForm()}</div>
      </ConditionalRenderer>
    </div>
  );
}
